import React, { useState, useEffect } from "react"
import { Navigate } from "react-router-dom"
import { useKindeAuth } from "@kinde-oss/kinde-auth-react"
import { Loader } from "shared/ui/Loader/Loader.component"
import { KindeRolesClaim } from "types/kinde.types"
import { USER_ROLE } from "types/userRole.types"
import Superadmin from "routes/Superadmin"

/**
 * Protects the Superadmin route by checking if the user has super-admin privileges.
 * If the user is not a super-admin, redirects to the home page.
 * Shows a loader while checking authorization.
 *
 * @returns The Superadmin component if authorized, otherwise redirects to /home
 */
const SuperadminProtectedRoute: React.FC = () => {
  const { getClaim } = useKindeAuth()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false)

  useEffect(() => {
    const checkSuperAdminRole = async () => {
      try {
        const userRoles = (await getClaim("roles")) as KindeRolesClaim | null
        const hasSuperAdminRole = userRoles?.value?.some(
          (role) => role.key === USER_ROLE.SUPER_ADMIN
        )
        setIsSuperAdmin(hasSuperAdminRole || false)
      } catch (error) {
        console.error("Error checking super admin role:", error)
        setIsSuperAdmin(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkSuperAdminRole()
  }, [getClaim])

  if (isLoading) {
    return (
      <div className="absolute left-2/4 top-2/4">
        <Loader fontSize={48} />
      </div>
    )
  }

  return isSuperAdmin ? <Superadmin /> : <Navigate to="/home" replace />
}

export default SuperadminProtectedRoute
